import Playlist from '@/utils/player/playlist';
import PlayerLog from '@/utils/player/log';
import AudioPlayer from '@/utils/player/audio';
import VideoPlayer from '@/utils/player/video';
import AutoNextSettings from '@/utils/player/auto-next';
import EventHandler from '@/utils/event';
import { PROGRAM_AUDIO_MEDIA_TYPE, PROGRAM_VIDEO_MEDIA_TYPE } from '@/utils/constant';
import {
  getFromStorage,
  getPlayerLastProgramIdStorageName,
  getSpeedPlayStorageName,
  setToStorage,
} from '@/utils';

class Players {
  constructor(options = {}) {
    const {
      list = [],
      loop = false,
      logUrl = {},
    } = options;

    // 播放列表
    this.playlist = new Playlist({
      list,
      loop,
    });

    // 播放列表缓存
    this.playlistStorage = list;

    // log处理器
    this.logHandler = new PlayerLog({
      logUrl,
    });

    // 音频播放器
    this.initAudioPlayer();

    // 视频播放器
    this.initVideoPlayer();

    // 事件处理器
    this.eventHandler = new EventHandler();

    // 自动下一首设置
    this.autoNextSettings = new AutoNextSettings();

    // 播放中ID
    this.playingId = 0;

    // 播放器加载状态
    this.loadingStatus = '';
  }

  /**
   * 设置播放ID
   * @param value
   */
  setPlayingId(value) {
    this.playingId = value;
  }

  /**
   * 设置播放器加载状态
   * @param value
   */
  setLoadingStatus(value) {
    this.loadingStatus = value;
  }

  /**
   * 获取播放器加载状态
   * @returns {string|*}
   */
  getLoadingStatus() {
    return this.loadingStatus;
  }

  /**
   * 获取自动下一首设置
   * @returns {*}
   */
  getAutoNext() {
    return this.autoNextSettings.get();
  }

  /**
   * 获取当前播放资源
   */
  getCurrentItem() {
    return this.playlist.getCurrentItem();
  }

  /**
   * 获取当前播放器时间
   * @returns {*}
   */
  getCurrentTime() {
    // 获取当前播放item
    const item = this.playlist.getCurrentItem();
    if (item == null) {
      return 0;
    }
    // 获取媒体类型
    const { playerMediaType = PROGRAM_AUDIO_MEDIA_TYPE } = item;
    // 根据媒体类型获取当前播放器时间
    if (playerMediaType === PROGRAM_AUDIO_MEDIA_TYPE) {
      return this.getAudioCurrentTime();
    }
    return 0;
  }

  /**
   * 获取音频播放器当前时间
   * @returns {*}
   */
  getAudioCurrentTime() {
    return this.audioPlayer.getCurrentTime();
  }

  /**
   * 设置播放完成数量
   * @param value
   */
  setFinishedCount(value) {
    this.finishedCount = value;
  }

  /**
   * 初始化音频播放器
   */
  initAudioPlayer() {
    const { logHandler } = this;

    // 音频播放器
    this.audioPlayer = new AudioPlayer({
      logHandler,
    });

    // 已播放总数量
    this.finishedCount = 0;

    // 监听事件
    this.setupAudioEventListener();
  }

  /**
   * 设置音频事件监控
   */
  setupAudioEventListener() {
    const { audioPlayer, finishedCount } = this;

    // 播放错误
    audioPlayer.on('error', () => {
      // 设置加载状态
      this.setLoadingStatus('error');

      // 重置播放ID
      this.setPlayingId(0);
    });

    // 等待播放
    audioPlayer.on('waiting', () => {
      // 设置加载状态
      this.setLoadingStatus('loading');
    });

    // 可以播放
    audioPlayer.on('canplaythrough', () => {
      // 设置加载状态
      this.setLoadingStatus('loaded');
    });

    // 开始播放
    audioPlayer.on('play', (e, source) => {
      const { id } = source;

      // 设置播放ID
      this.setPlayingId(id);

      // 触发播放开始事件
      this.eventHandler.emit('audioPlay', source);
    });

    // 暂停播放
    audioPlayer.on('pause', () => {
      // 重置播放ID
      this.setPlayingId(0);
    });

    // 播放结束
    audioPlayer.on('ended', (e, source) => {
      // 重置播放ID
      this.setPlayingId(0);

      // 更新已播放总数量
      const count = finishedCount + 1;
      this.setFinishedCount(count);

      // 更新自动下一首设置
      this.autoNextSettings.update(count);

      // 获取自动播放设置
      const autoNext = this.autoNextSettings.get();

      // 触发播放结束事件
      this.eventHandler.emit('audioEnded', source);

      // 可自动播放
      if (autoNext) {
        // 获取下一首播放指针
        const nextIndex = this.playlist.getNextIndex();

        // 有下一首播放指针，直接播放
        if (nextIndex > -1) {
          // 播放下一首
          this.play(nextIndex);
          return;
        }

        // 触发可自动播放下一首，交有业务层处理
        this.eventHandler.emit('canAutoNext', source);
      }
    });

    // 播放被终止
    audioPlayer.on('abort', () => {
      // 重置播放ID
      this.setPlayingId(0);
    });

    // 播放中
    audioPlayer.on('timeupdate', () => {
      this.eventHandler.emit('audioTimeUpdate');
    });

    // 播放器跳到指定位置
    audioPlayer.on('seeked', () => {
      this.eventHandler.emit('audioSeeked');
    });

    // 课时完成
    audioPlayer.on('lessonComplete', (params) => {
      this.eventHandler.emit('lessonComplete', params);
    });
  }

  /**
   * 初始化视频播放器对象
   */
  initVideoPlayer() {
    const { logHandler } = this;
    this.videoPlayer = new VideoPlayer({
      logHandler,
    });

    // 监听事件
    this.setupVideoEventListener();
  }

  setupVideoEventListener() {
    const { videoPlayer, finishedCount } = this;

    // 播放错误
    videoPlayer.on('error', () => {
      // 设置加载状态
      this.setLoadingStatus('error');

      // 重置播放ID
      this.setPlayingId(0);
    });

    // 等待播放
    videoPlayer.on('waiting', () => {
      // 设置加载状态
      this.setLoadingStatus('loading');
    });

    // 可以播放
    videoPlayer.on('canplaythrough', () => {
      // 设置加载状态
      this.setLoadingStatus('loaded');
    });

    // 开始播放
    videoPlayer.on('play', (e, source) => {
      const { id } = source;

      // 设置播放ID
      this.setPlayingId(id);

      // 触发播放开始事件
      this.eventHandler.emit('videoPlay', source);
    });

    // 暂停播放
    videoPlayer.on('pause', () => {
      // 重置播放ID
      this.setPlayingId(0);
    });

    // 播放结束
    videoPlayer.on('ended', (e, source) => {
      // 重置播放ID
      this.setPlayingId(0);

      // 更新已播放总数量
      const count = finishedCount + 1;
      this.setFinishedCount(count);

      // 更新自动下一首设置
      this.autoNextSettings.update(count);

      // 触发播放结束事件
      this.eventHandler.emit('videoEnded', source);
    });

    // 播放被终止
    videoPlayer.on('abort', () => {
      // 重置播放ID
      this.setPlayingId(0);
    });

    // 播放中
    videoPlayer.on('timeupdate', () => {
      this.eventHandler.emit('videoTimeUpdate');
    });

    // 播放器跳到指定位置
    videoPlayer.on('seeked', () => {
      this.eventHandler.emit('videoSeeked');
    });

    // 课时完成
    videoPlayer.on('lessonComplete', (params) => {
      this.eventHandler.emit('lessonComplete', params);
    });
  }

  /**
   * 设置视频播放器
   * @param options
   */
  setVideoPlayer(options) {
    this.videoPlayer.initPlayer(options);
  }

  /**
   * 检查播放列表是否有变化
   * @param list
   */
  checkIsDifferentPlaylist(list) {
    // 获取播放列表缓存
    const { playlistStorage } = this;

    // 判断数组长度
    const { length: storageLength } = playlistStorage;
    const { length } = list;
    if (length !== storageLength) return true;

    let result = false;
    // 遍历缓存以判断是否相同
    playlistStorage.forEach((item, index) => {
      if (item.id !== list[index].id || item.playerMediaType !== list[index].playerMediaType) {
        result = true;
      }
    });
    return result;
  }

  /**
   * 更新播放列表
   * @param list
   */
  updatePlaylist(list) {
    const diff = this.checkIsDifferentPlaylist(list);
    if (!diff) return;
    // 更新播放列表
    this.playlist.updateList(list);
    // 更新播放列表缓存
    this.playlistStorage = list;
  }

  /**
   * 通过ID获取播放列表中index
   * @param id
   * @returns {number}
   */
  getPlaylistIndexById(id) {
    return this.playlist.getIndexById(id);
  }

  /**
   * 进行播放
   * @param index
   */
  play(index = 0) {
    // 更新播放指针
    this.playlist.updateIndex(index);

    // 获取即将播放资源
    const next = this.playlist.getItemByIndex(index);

    if (next == null) return;

    // 获取即将播放资源ID
    const { id: nextId } = next;

    // 获取当前播放ID
    const { playingId } = this;

    // 正在播放当前资源，直接return 函数
    if (nextId === playingId) return;

    // 获取数据
    const item = this.playlist.getCurrentItem();
    if (item == null) return;

    // 获取媒体类型
    const { playerMediaType = PROGRAM_AUDIO_MEDIA_TYPE } = item;

    // 缓存播放的节目ID
    setToStorage(getPlayerLastProgramIdStorageName(), item.id);

    // 根据媒体类型进行播放
    // 音频类型
    if (playerMediaType === PROGRAM_AUDIO_MEDIA_TYPE) {
      // 暂停视频
      this.pauseVideo();
      // 对视频播放器进行打点
      this.videoPlayer.logPlayProgress();
      // 播放音频
      this.playAudio(item);

      // 设置播放速率
      const rate = getFromStorage(getSpeedPlayStorageName()) || 1;
      this.rate(rate);
      return;
    }

    // 视频类型
    if (playerMediaType === PROGRAM_VIDEO_MEDIA_TYPE) {
      const {
        id,
        type,
        duration,
        url,
        pic,
        videoType,
        danmaku,
        audioId,
      } = item;
      // 暂停音频
      this.pauseAudio(item);
      // 对音频播放器进行打点
      this.audioPlayer.logPlayProgress();
      const video = {
        id,
        type,
        duration,
        url,
        pic,
        videoType,
        audioId,
      };
      // 播放视频
      this.playVideo(video, danmaku);
    }
  }

  /**
   * 播放音频
   * @param item
   */
  playAudio(item) {
    // 调用音频播放器播放
    this.audioPlayer.play(item);
  }

  /**
   * 播放视频
   * @param video
   * @param danmaku
   */
  playVideo(video, danmaku) {
    this.videoPlayer.play(video, danmaku);
  }

  /**
   * 暂停播放
   */
  pause() {
    // 获取数据
    const item = this.playlist.getCurrentItem();
    if (item == null) return;
    // 获取媒体类型
    const { playerMediaType = PROGRAM_AUDIO_MEDIA_TYPE } = item;
    // 根据媒体类型进行暂停
    if (playerMediaType === PROGRAM_AUDIO_MEDIA_TYPE) {
      this.pauseAudio();
      return;
    }
    if (playerMediaType === PROGRAM_VIDEO_MEDIA_TYPE) {
      this.pauseVideo();
    }
  }

  /**
   * 暂停音频播放
   */
  pauseAudio() {
    this.audioPlayer.pause();
  }

  /**
   * 暂停视频播放
   */
  pauseVideo() {
    this.videoPlayer.pause();
  }

  /**
   * 设置播放器播放速率
   * @param rate
   */
  rate(rate) {
    // 获取数据
    const item = this.playlist.getCurrentItem();
    if (item == null) return;
    // 获取媒体类型
    const { playerMediaType = PROGRAM_AUDIO_MEDIA_TYPE } = item;
    // 根据媒体类型进行速率调节
    if (playerMediaType === PROGRAM_AUDIO_MEDIA_TYPE) {
      this.rateAudio(rate);
      return;
    }
    if (playerMediaType === PROGRAM_VIDEO_MEDIA_TYPE) {
      this.rateVideo();
    }
  }

  /**
   * 设置音频播放器播放速率
   * @param rate
   */
  rateAudio(rate) {
    this.audioPlayer.rate(rate);
  }

  /**
   * 设置视频播放器播放速率
   * @param rate
   */
  rateVideo(rate) {
    this.videoPlayer.rate(rate);
  }

  /**
   * 跳转播放器时间
   * @param time
   */
  seek(time) {
    // 获取数据
    const item = this.playlist.getCurrentItem();
    if (item == null) return;
    // 获取媒体类型
    const { playerMediaType = PROGRAM_AUDIO_MEDIA_TYPE } = item;
    // 根据媒体类型进行速率调节
    if (playerMediaType === PROGRAM_AUDIO_MEDIA_TYPE) {
      this.seekAudio(time);
      return;
    }
    if (playerMediaType === PROGRAM_VIDEO_MEDIA_TYPE) {
      this.seekVideo(time);
    }
  }

  /**
   * 跳转音频播放器时间
   * @param time
   */
  seekAudio(time) {
    this.audioPlayer.seek(time);
  }

  /**
   * 跳转视频播放器时间
   * @param time
   */
  seekVideo(time) {
    this.videoPlayer.seek(time);
  }

  /**
   * 播放进度打点
   * @param id
   * @param data
   */
  logPlayProgress(id, data) {
    return new Promise(async (resolve, reject) => {
      try {
        await this.logHandler.logPlayProgress(id, data);
        resolve();
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * 监听事件
   * @param type
   * @param fn
   */
  on(type, fn) {
    return this.eventHandler.on(type, fn);
  }

  /**
   * 只监听一次事件
   * @param type
   * @param fn
   * @returns {*}
   */
  once(type, fn) {
    return this.eventHandler.once(type, fn);
  }

  /**
   * 取消事件监听
   * @param type
   * @param fn
   * @returns {undefined}
   */
  off(type, fn) {
    return this.eventHandler.off(type, fn);
  }
}

export default Players;
